import React, { PureComponent } from 'react';

import { parse, stringify } from 'qs';
import { injectIntl, InjectedIntl } from 'react-intl';

import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';
import { colors } from '@atlaskit/theme';
import Tooltip from '@atlaskit/tooltip';

import { TableHeader } from 'src/components/pageable-table';

import { Sort } from '../../types';

import { sortMessages } from './branch-sort-link.i18n';
import * as styles from './branch-sort-link.styled';

type Props = {
  intl: InjectedIntl;
  label: string;
  sort: Sort;
  field: string;
  isDisabled: boolean;
  onSortChange: (sort: Sort) => void;
  desktopOnly?: boolean;
  width?: string;
  textAlign?: string;
  ariaLabelText: string;
};

type State = {
  order: SORT_ORDER;
};

enum SORT_ORDER {
  ASC = 'ASC',
  DESC = 'DESC',
}

const SORTS = Object.freeze({
  name: {
    ASC: 'name',
    DESC: '-name',
  },
  ahead: {
    ASC: 'ahead',
    DESC: '-ahead',
  },
  behind: {
    ASC: 'behind',
    DESC: '-behind',
  },
  'target.date': {
    ASC: 'target.date',
    DESC: '-target.date',
  },
});

class SortableTableHeader extends PureComponent<Props, State> {
  // @ts-ignore TODO: fix noImplicitAny error here
  constructor(props) {
    super(props);

    const { field, order } = this.parseSort();
    this.state = {
      order: field === props.field ? order : SORT_ORDER.DESC,
    };
  }

  parseSort = () => {
    const { sort } = this.props;
    return sort[0] === '-'
      ? {
          field: sort.slice(1),
          order: SORT_ORDER.DESC,
        }
      : {
          field: sort,
          order: SORT_ORDER.ASC,
        };
  };

  buildSort = () => {
    const { field } = this.props;
    const { order } = this.state;
    // @ts-ignore TODO: fix noImplicitAny error here
    return SORTS[field][order];
  };

  handleSortChange = () => {
    this.setState(
      ({ order }) => ({
        order: order === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC,
      }),
      () => {
        this.props.onSortChange(this.buildSort());
      }
    );
  };

  handleKeyPress = (event: React.KeyboardEvent) => {
    event.preventDefault();

    const { isDisabled } = this.props;
    if (!isDisabled && event.key === 'Enter') {
      this.handleSortChange();
    }
  };

  handleClick = (event: React.SyntheticEvent) => {
    event.preventDefault();
    this.handleSortChange();
  };

  generateHref = () => {
    const { field } = this.props;
    const { order } = this.state;
    const inverse = order === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC;
    // @ts-ignore TODO: fix noImplicitAny error here
    const nextSort = SORTS[field][inverse];

    const { pathname, search } = window.location;
    const params = parse(search, { ignoreQueryPrefix: true });
    const nextParams = {
      ...params,
      sort: nextSort,
    };
    const nextQuery = stringify(nextParams, { skipNulls: true });
    return `${pathname}?${nextQuery}`;
  };

  render() {
    const {
      intl,
      label,
      field,
      isDisabled,
      desktopOnly,
      width,
      textAlign,
      ariaLabelText,
    } = this.props;
    const { order } = this.state;

    const { field: sortingBy } = this.parseSort();
    const isActive = field === sortingBy;

    const ariaSort = isActive
      ? order === 'ASC'
        ? 'ascending'
        : 'descending'
      : undefined;

    const ariaLabel = isActive
      ? intl.formatMessage(sortMessages[order].activeSortLabel, {
          sortLabel: label,
        })
      : undefined;

    const Icon = order === SORT_ORDER.ASC ? ChevronUpIcon : ChevronDownIcon;

    return (
      <TableHeader
        aria-sort={ariaSort}
        aria-label={ariaLabelText}
        role="columnheader"
        desktopOnly={!!desktopOnly}
        width={width}
        textAlign={textAlign}
        scope="col"
      >
        <Tooltip content={ariaLabelText}>
          <styles.SortLink
            href={this.generateHref()}
            isDisabled={isDisabled}
            aria-label={ariaLabel}
            onClick={this.handleClick}
            onKeyPress={this.handleKeyPress}
          >
            {label}
            <Icon
              primaryColor={isActive ? colors.N500 : colors.N60}
              label={intl.formatMessage(sortMessages[order].iconLabel)}
            />
          </styles.SortLink>
        </Tooltip>
      </TableHeader>
    );
  }
}

export default injectIntl(SortableTableHeader);
